import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { loggedInApiCall, resolveRequestLocale } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { isQueryReady } from "src/api/axiosClient/utils";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

import { IngredientReferenceUnified, IngredientReferenceUnifiedSchema, SURE_API_DEGRADED_HEADER } from "./types";

export const getOneIngredientReferenceUnifiedKey = "oneIngredientReferenceUnified" as const;

const IngredientReferenceUnifiedItemCollectionSchema = z.object({
    "hydra:member": IngredientReferenceUnifiedSchema.array(),
    "hydra:totalItems": z.number().int().nonnegative(),
});

/**
 * Fetch a single unified ingredient reference by composite id. Returns `undefined` for the
 * `compositeId === undefined` case so consumers can short-circuit without a network call.
 */
export const useGetOneIngredientReferenceUnified = (compositeId: string | undefined) => {
    const { selectedCompany } = useFoodpilotContext();
    const { i18n } = useTranslation();
    const locale = resolveRequestLocale(undefined, i18n.resolvedLanguage);
    const organizationId = selectedCompany?.id;
    const isEnabled = organizationId !== undefined && compositeId !== undefined && compositeId !== "";

    const query = useQuery({
        enabled: isEnabled,
        queryKey: [getOneIngredientReferenceUnifiedKey, organizationId ?? null, locale, compositeId],
        keepPreviousData: false,
        queryFn: async () => {
            if (organizationId === undefined || compositeId === undefined || compositeId === "") {
                throw new Error("Cannot load an ingredient reference without its tenant scope and composite id");
            }

            const response = await loggedInApiCall.get("/api/ingredient-references-unified", {
                params: {
                    id: [compositeId],
                    itemsPerPage: 1,
                    organization_id: organizationId,
                    page: 1,
                },
            });

            if (compositeId.startsWith("sure-") && response.headers?.[SURE_API_DEGRADED_HEADER] === "1") {
                throw new Error(`Sure ingredient reference lookup is degraded for ${compositeId}`);
            }

            const collection = zodParseWithLog(
                response.data,
                IngredientReferenceUnifiedItemCollectionSchema,
                getOneIngredientReferenceUnifiedKey,
            );

            const references = collection["hydra:member"];
            const totalItems = collection["hydra:totalItems"];
            if (references.length !== totalItems || references.length > 1) {
                throw new Error(`Unexpected unified ingredient reference cardinality for ${compositeId}`);
            }

            const reference = references[0];
            if (reference !== undefined && reference.compositeId !== compositeId) {
                throw new Error(`Unified ingredient reference scope mismatch for ${compositeId}`);
            }

            return reference;
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        ...query,
        data: isEnabled ? query.data : undefined,
        ingredientReference: isEnabled ? (query.data as IngredientReferenceUnified | undefined) : undefined,
        isReady,
    };
};
